import { CardContainer } from 'finara-components';

import type { AssessmentResult } from '@/types/riskAssessment';
import { getRiskLevelColor, getRiskLevelLabel } from '@/utils/riskCalculations';

interface RiskMatrixProps {
  categoryScores: AssessmentResult['categoryScores'];
}

export function RiskMatrix({ categoryScores }: RiskMatrixProps) {
  return (
    <CardContainer>
      <div className="p-6">
        <h2 className="text-xl font-bold text-brand-white mb-4">Matriz de Riesgo por Categoría</h2>
        <p className="text-sm text-muted mb-6">
          Visualización del nivel de riesgo en cada categoría evaluada.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {categoryScores.map(categoryScore => {
            const percentage = (categoryScore.score / categoryScore.maxScore) * 100;

            return (
              <div
                key={categoryScore.categoryId}
                className="border border-border rounded-lg p-4 bg-white/5"
              >
                <h3 className="font-semibold text-brand-white mb-2">{categoryScore.categoryName}</h3>

                {/* Progress Bar */}
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-muted mb-1">
                    <span>Puntuación</span>
                    <span>
                      {categoryScore.score} / {categoryScore.maxScore}
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${
                        categoryScore.riskLevel === 'low'
                          ? 'bg-green-500'
                          : categoryScore.riskLevel === 'medium'
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>

                {/* Risk Level Badge */}
                <div
                  className={`px-3 py-2 rounded-lg border text-center font-medium ${getRiskLevelColor(categoryScore.riskLevel)}`}
                >
                  {getRiskLevelLabel(categoryScore.riskLevel)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Heat Map Legend */}
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-brand-white mb-3">Leyenda</h4>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500" />
              <span className="text-sm text-muted">Riesgo Bajo (≤40%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500" />
              <span className="text-sm text-muted">Riesgo Medio (41-70%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500" />
              <span className="text-sm text-muted">Riesgo Alto (&gt;70%)</span>
            </div>
          </div>
        </div>
      </div>
    </CardContainer>
  );
}
